import { useStrings } from '@/i18n/Localization';
import type { StringKey } from '@/i18n/strings';
import type { ConversationRecord, MessageRecord, OutboxRecord } from '@/store/types';
import styles from '@/components/SendIndicator.module.css';

/**
 * The Section 14 send indicators, exactly as specified:
 *
 *   clock                    locally queued
 *   spinner                  attempting
 *   single check             committed
 *   double check             delivered
 *   highlighted double check read, when privacy allows
 *   error icon               terminal failure, with a retry action
 *
 * Each state has a distinct *shape* and a distinct screen-reader label, not
 * only a distinct colour or animation. Section 14's accessibility gate forbids
 * motion-only information, and the same argument rules out colour-only
 * information: the read state is a double check that is filled as well as
 * tinted, so it is still distinguishable in monochrome.
 */
export type SendState = 'queued' | 'sending' | 'committed' | 'delivered' | 'read' | 'failed';

const LABELS: Readonly<Record<SendState, StringKey>> = {
  queued: 'status.queued',
  sending: 'status.sending',
  committed: 'status.committed',
  delivered: 'status.delivered',
  read: 'status.read',
  failed: 'status.failed',
};

/**
 * Decides which indicator a message deserves.
 *
 * The outbox record is authoritative while one exists, because it is the only
 * thing that knows whether a request is actually on the wire. Once it is gone
 * the conversation's peer positions decide, and those are monotonic, so a
 * message never moves backwards from read to delivered.
 */
export function sendStateFor(
  message: MessageRecord,
  outbox: OutboxRecord | undefined,
  conversation: ConversationRecord | null,
): SendState {
  if (outbox) {
    switch (outbox.state) {
      case 'QUEUED':
        return 'queued';
      case 'SENDING':
        return 'sending';
      case 'FAILED':
        return 'failed';
      case 'COMMITTED':
        break;
    }
  }
  if (message.localState === 'FAILED') {
    return 'failed';
  }
  if (message.localState === 'PENDING') {
    return 'queued';
  }
  if (conversation && message.createdSeq > 0) {
    if (conversation.peerReadSeq >= message.createdSeq) {
      return 'read';
    }
    if (conversation.peerDeliveredSeq >= message.createdSeq) {
      return 'delivered';
    }
  }
  return 'committed';
}

function Glyph({ state }: { state: SendState }) {
  switch (state) {
    case 'queued':
      // A clock face: a circle with hands, readable at 12 px.
      return (
        <svg viewBox="0 0 16 16" className={styles.glyph} aria-hidden="true" focusable="false">
          <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M8 4.5V8l2.5 1.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'sending':
      return (
        <svg viewBox="0 0 16 16" className={styles.spinner} aria-hidden="true" focusable="false">
          <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
          <path d="M8 2a6 6 0 0 1 6 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      );
    case 'committed':
      return (
        <svg viewBox="0 0 16 16" className={styles.glyph} aria-hidden="true" focusable="false">
          <path d="M3 8.5 6.5 12 13 4.5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'delivered':
    case 'read':
      return (
        <svg viewBox="0 0 20 16" className={styles.glyph} aria-hidden="true" focusable="false">
          <path d="M2 8.5 5.5 12 12 4.5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M8 8.5 11.5 12 18 4.5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'failed':
      return (
        <svg viewBox="0 0 16 16" className={styles.glyph} aria-hidden="true" focusable="false">
          <circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M8 4.75v4" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
          <circle cx="8" cy="11.25" r="0.9" fill="currentColor" />
        </svg>
      );
  }
}

export function SendIndicator({ state }: { state: SendState }) {
  const { t } = useStrings();
  const label = t(LABELS[state]);

  return (
    <span
      className={`${styles.indicator} ${state === 'read' ? styles.read : ''} ${state === 'failed' ? styles.failed : ''}`}
      role="img"
      aria-label={label}
      title={label}
    >
      <Glyph state={state} />
    </span>
  );
}
